import axios from "axios";
import qs from "qs";
import { getBranchUuid } from "@/lib/Config";
import { getAccessToken, getUserId, getUserUuid } from "@/lib/UserInfo";
import router from '@/router';
import { LOCAL_STORAGE_HVM_USERINFO } from "@/lib/Constant";
import { SessionManager } from "@/lib/SessionManager";

// 세션 만료 처리 중복 방지를 위한 플래그
let isSessionExpiredHandling = false;

// Axios 인스턴스 생성
const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL,
});

// 요청 인터셉터 추가
apiClient.interceptors.request.use(async (config) => {
  const token = getAccessToken();
  if (token) {
      config.headers.Authorization = `Bearer ${token}`;
  }

  // 히스토리 기록 요청
  // 프리렌더 중에는 접속 이력을 남기지 않는다.
  if(getBranchUuid() != null && !window.__PRERENDER_INJECTED){
    await logHistory(config);
  }

  return config;
}, (error) => {
    return Promise.reject(error);
});

// 응답 인터셉터 추가 - 401 에러 처리
apiClient.interceptors.response.use(
  response => response,
  async (error) => {
    // 401 에러 처리 (중복 방지)
    if (error.response && error.response.status === 401 && !isSessionExpiredHandling) {
      console.log('401 에러 감지 - 세션 만료 처리 시작');
      await handleSessionExpired();
      return Promise.reject(error);
    }
    
    // 401이 아닌 다른 에러는 그대로 전파
    return Promise.reject(error);
  }
);

// 세션 만료 처리 함수
const handleSessionExpired = async () => {
  if (isSessionExpiredHandling) {
    console.log('AxiosCall: 이미 세션 만료 처리 중...');
    return;
  }

  isSessionExpiredHandling = true;
  console.log('AxiosCall: 세션 만료 처리 시작');
  
  try {
    // 1. SessionManager를 통한 로그아웃 처리
    SessionManager.logout();
    
    // 2. 세션 만료 AlertPopup 표시
    showSessionExpiredAlert();
    
    console.log('AxiosCall: 세션 만료 처리 완료');
  } catch (error) {
    console.error('AxiosCall: 세션 만료 처리 중 오류:', error);
  } finally {
    // 3초 후 플래그 초기화 (중복 방지 해제)
    setTimeout(() => {
      isSessionExpiredHandling = false;
    }, 3000);
  }
};

// 세션 만료 AlertPopup 표시 함수
const showSessionExpiredAlert = () => {
  // Vue 3 환경에서 전역 이벤트 발생 (eventBus 대신)
  if (window.Vue && window.Vue.config && window.Vue.config.globalProperties) {
    // Vue 3 방식
    const app = window.Vue.config.globalProperties;
    if (app.$root) {
      app.$root.$emit('show-session-expired-alert');
    }
  } else {
    // 직접 AlertPopup 표시 (fallback)
    if (confirm('로그인이 만료되었습니다.\n다시 로그인하시겠습니까?')) {
      router.push('/HFO_LO_001?reason=session-expired');
    }
  }
};

// key를 인자로 받아 query string 또는 request body에서 값을 추출하는 함수
const getValueByKey = (config, key) => {
  // query string에서 key 추출
  const url = new URL(config.url, window.location.origin);
  const queryParams = new URLSearchParams(url.search);
  const valueFromQuery = queryParams.get(key);

  // request body에서 key 추출
  let valueFromBody = null;
  if (config.data) {
      try {
          const requestBody = typeof config.data === 'string' ? JSON.parse(config.data) : config.data;
          valueFromBody = requestBody[key] || null;
      } catch (error) {
          console.error('Failed to parse request body:', error);
      }
  }

  return valueFromQuery || valueFromBody;
};

// 히스토리 기록 함수
const logHistory = async (config, isError = false) => {
  const historyData = {
      host: `${location.protocol}//${location.host}`,
      referer: document.referrer,
      menu: '',
      userUuid: getUserUuid(),
      userId: getUserId(),
      page: window.location.pathname,
      action: isError ? 'ERROR' : getAction(config.method),
      cookie: document.cookie,
      bannerUuid: getValueByKey(config, 'bannerUuid'),
      brandUuid: getValueByKey(config, 'brandUuid'),
      storeUuid: getValueByKey(config, 'storeUuid'),
  };

  console.log('히스토리 기록 요청:', historyData);
};

// 액션 반환 함수
const getAction = (method) => {
  switch (method.toLowerCase()) {
      case 'get': return 'GET';
      case 'post': return 'POST';
      case 'put': return 'PUT';
      case 'delete': return 'DELETE';
      default: return 'UNKNOWN';
  }
};

// 쿼리 스트링 변환 함수
const toQueryString = (data) => {
  if (typeof data === "object" && data !== null) {
    if (data.branchUuid === null) {
      // 명시적으로 null 을 넘기면 지점 필터 없이 호출한다 (브랜드 UUID 교차 조회 등).
      delete data.branchUuid;
    } else {
      const branchUuid = getBranchUuid();
      if (branchUuid && !data.branchUuid) {
        data.branchUuid = branchUuid;
      }
    }

    if (Object.keys(data).length > 0) {
      return "?" + new URLSearchParams(data).toString();
    }
  }
  return "";
};

axios.defaults.paramsSerializer = (params) => {
  return qs.stringify(params);
};

// GET 요청 함수
export const getAxios = async (url, data = {}, retryCount = 0) => {
  const branchUuid = getBranchUuid();
  const needsBranch = !url.includes("/common/config") && !(data && data.branchUuid === null);

  // config(branchUuid) 가 아직 없으면 최대 3회(300ms 간격) 기다린다.
  // 지점 필터 없이 나간 요청은 다른 지점 데이터를 돌려주므로 기다리는 편이 맞다.
  if (!branchUuid && needsBranch && retryCount < 3) {
    console.log('branchUuid가 null입니다. 1초 후 재시도합니다...');
    
    return new Promise((resolve, reject) => {
      setTimeout(async () => {
        try {
          const result = await getAxios(url, data, retryCount + 1);
          resolve(result);
        } catch (error) {
          reject(error);
        }
      }, 300);
    });
  }

  if (!branchUuid && retryCount >= 3) {
    console.warn('branchUuid를 가져올 수 없어 요청을 계속 진행합니다.');
  }

  const queryString = toQueryString(data);

  try {
    const response = await apiClient.get(url + queryString);
    console.log('GET 요청 성공:', {
      url: url + queryString,
      retryCount
    });
    return response.data;
  } catch (error) {
    console.error('GET 요청 실패:', error);
    throw error; // 인터셉터에서 401 처리되므로 그대로 throw
  }
};

// GET Direct 요청 함수
export const getAxiosDirect = async (url, data) => {
  const queryString = toQueryString(data);

  try {
    const response = await apiClient.get(url + queryString);
    console.log('GET Direct 요청 성공');
    return response.data;
  } catch (error) {
    console.error('GET Direct 요청 실패:', error);
    throw error;
  }
};

// GET Custom 요청 함수
export const getAxiosCustom = async (url, data = {}, retryCount = 0) => {
  const branchUuid = getBranchUuid();
  
  if (!branchUuid && retryCount === 0) {
    console.log('branchUuid가 null입니다. 1초 후 재시도합니다...');
    
    return new Promise((resolve, reject) => {
      setTimeout(async () => {
        try {
          const result = await getAxiosCustom(url, data, retryCount + 1);
          resolve(result);
        } catch (error) {
          reject(error);
        }
      }, 500);
    });
  }

  if (!branchUuid && retryCount >= 3) {
    console.warn('branchUuid를 가져올 수 없어 요청을 계속 진행합니다.');
  }

  const queryString = toQueryString(data);

  try {
    const response = await apiClient.get(url + queryString);
    console.log('GET Custom 요청 성공:', {
      url: url + queryString,
      retryCount
    });
    return response;
  } catch (error) {
    console.error('GET Custom 요청 실패:', error);
    throw error;
  }
};

// POST 요청 함수
export const postAxios = async (url, data = {}, fileYn, retryCount = 0) => {
  const branchUuid = getBranchUuid();

  if (!branchUuid && retryCount === 0) {
    console.log('branchUuid가 null입니다. 1초 후 재시도합니다...');

    return new Promise((resolve, reject) => {
      setTimeout(async () => {
        try {
          const result = await postAxios(url, data, fileYn, retryCount + 1);
          resolve(result);
        } catch (error) {
          reject(error);
        }
      }, 500);
    });
  }

  if (!branchUuid && retryCount >= 3) {
    console.warn('branchUuid를 가져올 수 없어 요청을 계속 진행합니다.');
  }

  // branchUuid가 없으면 추가
  if (typeof data === "object" && data !== null && branchUuid && !data.branchUuid) {
    data.branchUuid = branchUuid;
  }

  let config = {};
  if (fileYn) {
    config.headers = { "Content-Type": "multipart/form-data" };
  }

  try {
    const response = await apiClient.post(url, data, config);
    console.log('POST 요청 성공');
    return response.data;
  } catch (error) {
    console.error('POST 요청 실패:', error);
    throw error; // 인터셉터에서 401 처리되므로 그대로 throw
  }
};

// PUT 요청 함수
export const putAxios = async (url, data) => {
  try {
    const response = await apiClient.put(url, data);
    console.log('PUT 요청 성공');
    return response.data;
  } catch (error) {
    console.error('PUT 요청 실패:', error);
    throw error;
  }
};

// DELETE 요청 함수
export const deleteAxios = async (url, data = {}, retryCount = 0) => {
  const branchUuid = getBranchUuid();
  
  if (!branchUuid && retryCount === 0) {
    console.log('branchUuid가 null입니다. 1초 후 재시도합니다...');
    
    return new Promise((resolve, reject) => {
      setTimeout(async () => {
        try {
          const result = await deleteAxios(url, data, retryCount + 1);
          resolve(result);
        } catch (error) {
          reject(error);
        }
      }, 1000);
    });
  }

  if (!branchUuid && retryCount >= 3) {
    console.warn('branchUuid를 가져올 수 없어 요청을 계속 진행합니다.');
  }

  const queryString = toQueryString(data);

  try {
    const response = await apiClient.delete(url + queryString);
    console.log('DELETE 요청 성공:', {
      url: url + queryString,
      retryCount
    });
    return response.data;
  } catch (error) {
    console.error('DELETE 요청 실패:', error);
    throw error;
  }
};

// 에러 처리 함수 (필요시 사용)
export const handleApiError = (error) => {
  const status = error.response ? error.response.status : null;

  switch (status) {
    case 401:
      console.log('401 에러 - 인터셉터에서 처리됨');
      break;
    case 403:
      console.log('403 에러 - 권한 없음');
      break;
    case 404:
      console.log('404 에러 - 페이지 찾을 수 없음');
      break;
    case 500:
      console.log('500 에러 - 서버 오류');
      break;
    default:
      console.error('API 요청 중 오류 발생:', error);
      break;
  }
  
  throw error;
};